import React, { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { getAccessToken } from "../../../storage/storage";
import { AiOutlineClose } from "react-icons/ai";
import { useFormik } from "formik";
import axios from "axios";
import * as Yup from "yup";
import { ButtonLoader4 } from "../../../components/ButtonLoader";
import Swal from "sweetalert2";

function CommentModal({ isOpenModal, setIsOpenModal, invoiceId, setInvoiceData }) {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const cancelButtonRef = useRef(null);

  const handleClose = () => {
    setIsOpenModal(false);
  };

  const MAX_CHARS = 500;

  const { values, errors, touched, handleChange, handleSubmit } = useFormik({
    initialValues: {
      comment: "",
    },
    validationSchema: Yup.object({
      comment: Yup.string()
        .required("Comment is required")
        .max(MAX_CHARS, `Comment must be at most ${MAX_CHARS} characters`),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const res = await axios.put(
          `${BASE_URL}/api/invoices/${invoiceId}/reject/`,
          {
            rejection_reason: values.comment,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        // update parent invoice data to show rejected + comment
        setInvoiceData((prev) => ({
          ...prev,
          status: "rejected",
          rejection_reason: values.comment, // add comment locally
        }));

        Swal.fire({
          icon: "success",
          title: "Invoice Rejected",
          timer: 2000,
          showConfirmButton: false,
          position: "center",
        });

        handleClose();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: err.response?.data?.detail || "Something went wrong!",
          timer: 2000,
          showConfirmButton: false,
          position: "center",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Transition.Root show={isOpenModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[99]"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex h-full items-center justify-center p-4 text-center xs:p-2">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all md:w-[800px] h-[55vh] w-full flex justify-center items-center">
                <div className="flex flex-col gap-8 mt-4 justify-center items-center w-full h-full overflow-y-scroll py-2 px-4">
                  <AiOutlineClose
                    className="absolute top-2 right-3 cursor-pointer"
                    onClick={handleClose}
                  />
                  <h1 className="font-semibold text-[#586989] text-2xl">
                    Reason for Invoice Rejection
                  </h1>

                  {/* Comments Section */}
                  <div className="flex bg-[#FFF5CE] rounded-lg border border-gray-300 shadow-sm w-[95%] flex-col px-4 py-3">
                    <h3 className="text-lg font-semibold text-[#586989]">
                      Comments
                    </h3>
                    <textarea
                      name="comment"
                      rows={3}
                      placeholder="Enter reason for rejection..."
                      value={values.comment}
                      onChange={handleChange}
                      className="mt-2 w-full p-2 border outline-none rounded-lg resize-none"
                      maxLength={MAX_CHARS}
                    />
                    <div className="flex justify-between text-sm mt-1">
                      <p className="text-red-500">
                        {touched.comment && errors.comment ? errors.comment : ""}
                      </p>
                      <p>
                        {values.comment.length}/{MAX_CHARS}
                      </p>
                    </div>
                  </div>

                  <div className="w-full flex justify-end px-4">
                    <button
                      onClick={handleSubmit}
                      type="submit"
                      disabled={loading}
                      className="mt-0 w-[100px] bg-[#586989] text-white h-[40px] rounded"
                    >
                      {loading ? <ButtonLoader4 /> : "Submit"}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default CommentModal;
